import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { CpeInterfaces } from '@bumble/app/types/cpeTypes';

interface RouterNetworkInfoEditProps {
  onSubmit: () => void;
  cpeId: string;
  prodCode: string;
  mode: string;
  ports: CpeInterfaces | undefined
}

export const RouterNetworkInfoEdit = (
  { onSubmit, cpeId, prodCode, mode, ports }: RouterNetworkInfoEditProps
) => {
  const api = useApi();
  const [routerMode, setRouterMode] = useState<boolean>(mode === 'ROUTER');
  const [newDhcpClient, setNewDhcpClient] = useState<boolean>(false);
  const [newCidr, setNewCidr] = useState<string>('');
  const [newGwIp, setNewGwIp] = useState<string>('');
  const [newWanDnsIp, setNewWanDnsIp] = useState<string>('');
  const [newSnat, setNewSnat] = useState<boolean>(false);
  const [newLinkUpSpeed, setNewLinkUpSpeed] = useState<string>('0');
  const [newLinkDownSpeed, setNewLinkDownSpeed] = useState<string>('0');
  const [newLanCidr, setNewLanCidr] = useState<string>('');
  const [newDhcpServer, setNewDhcpServer] = useState<boolean>(false);
  const [newDhcpIpS, setNewDhcpIpS] = useState<string>('');
  const [newDhcpIpE, setNewDhcpIpE] = useState<string>('');
  const [newDnsIp, setNewDnsIp] = useState<string>('');

  useEffect(() => {
    if (ports) {
      let wan: any = {}, lan: any = {}, mgmt: any = {};
      for (let itf in ports) {
        if (ports[itf] && ports[itf]['role'].toLowerCase().startsWith('lan')) {
          lan = ports[itf];
        }
        if (ports[itf] && ports[itf]['role'].toLowerCase().startsWith('wan')) {
          wan = ports[itf];
        }
        if (itf === 'mgmt') mgmt = ports[itf];
      }
      if (prodCode !== 'BN_CPE_100') {
        setNewLinkUpSpeed(wan.tx_mbps || '0');
        setNewLinkDownSpeed(wan.rx_mbps || '0');
      }
      if (mode === 'PASSTHROUGH') {
        setNewDhcpClient(mgmt.dhcp ? true : false);
        setNewCidr(mgmt.cidr_addr || '');
        setNewGwIp(mgmt.default_gw_ip || '');
        setNewWanDnsIp(mgmt.dns_ip || '');
      } else {
        setNewDhcpClient(wan.dhcp ? true : false);
        setNewCidr(wan.cidr_addr || '');
        setNewGwIp(wan.default_gw_ip || '');
        setNewWanDnsIp(wan.dns_ip || '');
        setNewSnat(wan.snat ? true : false);
        setNewLanCidr(lan.cidr_addr || '');
        setNewDhcpServer(lan.dhcp ? true : false);
        setNewDhcpIpS(lan.dhcp && lan.dhcp_ip_s ? lan.dhcp_ip_s : '');
        setNewDhcpIpE(lan.dhcp && lan.dhcp_ip_e ? lan.dhcp_ip_e : '');
        setNewDnsIp(lan.dhcp ? lan.dns_ip : '');
      }
    }
  }, []);

  const cpeInfoUpdatePost = useMutation(api.editCpeNetworkApiV1EditCpeNetworkPost, {
    onSuccess() {
      toast.success('Updated network interface successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  const validate = () => {
    if (isNaN(parseInt(newLinkUpSpeed))) {
      toast.error('Please enter a valid number for link up speed.');
      return false;
    }
    if (isNaN(parseInt(newLinkDownSpeed))) {
      toast.error('Please enter a valid number for link down speed.');
      return false;
    }
    return true;
  }

  const handleChangeMode = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setRouterMode(value === 'true');
  };

  const handleSubmit = () => {
    if (validate()) {
      cpeInfoUpdatePost.mutate({
        cpe_id: cpeId,
        ...(prodCode !== 'BN_CPE_100' && {
          wan_tx_mbps: parseInt(newLinkUpSpeed),
          wan_rx_mbps: parseInt(newLinkDownSpeed),
        }),
        ...(routerMode
          ? {
            mode: 'ROUTER',
            wan_dhcp_c: newDhcpClient,
            ...(!newDhcpClient && {
              wan_cidr_addr: newCidr,
              wan_default_gw_ip: newGwIp,
              wan_dns_ip: newWanDnsIp,
            }),
            wan_snat: newSnat,
            ...(newLanCidr && { lan_cidr_addr: newLanCidr }),
            dhcp_server: newDhcpServer,
            ...(newDhcpServer && {
              dhcp_ip_s: newDhcpIpS,
              dhcp_ip_e: newDhcpIpE,
              dns_ip: newDnsIp,
            }),
          }
          : {
            mode: 'PASSTHROUGH',
            mgmt_dhcp_c: newDhcpClient,
            ...(!newDhcpClient && {
              mgmt_cidr_addr: newCidr,
              mgmt_default_gw_ip: newGwIp,
              mgmt_dns_ip: newWanDnsIp,
            }),
          }
        ),
      });
    }
  };

  return (
    <Box pt={2}>
      {prodCode === 'BN-CPE-1000' && <>
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="Link Upload Speed (mbps)"
          variant="outlined"
          value={newLinkUpSpeed}
          onChange={(e) => setNewLinkUpSpeed(e.target.value)}
        />
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="Link Download Speed (mbps)"
          variant="outlined"
          value={newLinkDownSpeed}
          onChange={(e) => setNewLinkDownSpeed(e.target.value)}
        />
      </>}
      <Divider sx={{ my: 4 }} />
      <FormControl fullWidth sx={{ mb: 4 }}>
        <RadioGroup
          row
          name="router-mode"
          onChange={handleChangeMode}
          value={routerMode.toString()}
        >
          <FormControlLabel value="false" control={<Radio />} label="Pass through" />
          <FormControlLabel value="true" control={<Radio />} label="Router mode" />
        </RadioGroup>
      </FormControl>
      <Typography variant="body2" sx={{ mb: 4 }}>
        {routerMode ? 'WAN Interface (Eth0)' : 'Management Interface'}
      </Typography>
      <FormControlLabel
        sx={{ mb: 4 }}
        control={
          <Checkbox
            checked={newDhcpClient}
            onChange={(e) => setNewDhcpClient(e.target.checked)}
          />
        }
        label="DHCP Client"
      />
      {!newDhcpClient && <>
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="IP Address in CIDR format (Ex: A.B.C.D/24)"
          variant="outlined"
          value={newCidr}
          onChange={(e) => setNewCidr(e.target.value)}
          autoFocus
        />
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="Default Gateway IP"
          variant="outlined"
          value={newGwIp}
          onChange={(e) => setNewGwIp(e.target.value)}
        />
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="DNS Server IP(s)"
          variant="outlined"
          value={newWanDnsIp}
          onChange={(e) => setNewWanDnsIp(e.target.value)}
        />
      </>}
      {routerMode &&
        <FormControlLabel
          sx={{ mb: 4 }}
          control={
            <Checkbox
              checked={newSnat}
              onChange={(e) => setNewSnat(e.target.checked)}
            />
          }
          label="Enable Source NAT"
        />
      }
      {routerMode && <>
        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" sx={{ mb: 8 }}>
          LAN Interface (Eth1)
        </Typography>
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          id="lan_cidr_ip_address"
          label="IP Address in CIDR format (Ex: A.B.C.D/24)"
          variant="outlined"
          value={newLanCidr}
          onChange={(e) => setNewLanCidr(e.target.value)}
        />
        <FormControlLabel
          sx={{ mb: 4 }}
          control={
            <Checkbox
              checked={newDhcpServer}
              onChange={(e) => setNewDhcpServer(e.target.checked)}
            />
          }
          label="DHCP Server"
        />
        { newDhcpServer &&
          <>
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="DHCP IP Address Range Start"
              variant="outlined"
              value={newDhcpIpS}
              onChange={(e) => setNewDhcpIpS(e.target.value)}
              autoFocus
            />
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="DHCP IP Address Range End"
              variant="outlined"
              value={newDhcpIpE}
              onChange={(e) => setNewDhcpIpE(e.target.value)}
              autoFocus
            />
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="DNS Server IP(s)"
              variant="outlined"
              value={newDnsIp}
              onChange={(e) => setNewDnsIp(e.target.value)}
              autoFocus
            />
          </>
        }
      </>}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={!newDhcpClient && (!newCidr || !newGwIp || !newWanDnsIp)}>
          Save
        </Button>
      </Box>
    </Box>
  );
};
